import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LayoutDashboard, MessagesSquare, Network, GitCompareArrows, Route, Target, Sparkles, Library,
  FlaskConical, Presentation, Scale, Radar, FileBarChart2, ShieldCheck, HelpCircle, FileUp, Gauge,
  ClipboardCheck, ChevronDown, ChevronRight, PanelLeftClose, PanelLeft, Menu, X, Boxes, BarChart3 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const NAV_GROUPS = [
  { label: "Overview", items: [
    { id: "dashboard", label: "Executive Intelligence", icon: LayoutDashboard, tip: "The executive entry point — headline assessment, risks and opportunities" },
  ]},
  { label: "Discover & Understand", items: [
    { id: "questions", label: "Intelligence Questions", icon: HelpCircle, tip: "What this assessment was commissioned to answer" },
    { id: "feed", label: "HCP Voice", icon: MessagesSquare, tip: "What HCPs are saying — curated, source-linked statements" },
    { id: "themes", label: "Clinical Themes", icon: Network, tip: "How HCP views cluster into clinical themes" },
    { id: "adoption", label: "Adoption Journey", icon: Route, tip: "Where behaviour changes across the adoption pathway" },
  ]},
  { label: "Evidence & Context", items: [
    { id: "evidence", label: "Evidence Landscape", icon: FlaskConical, tip: "Published evidence mapped against HCP voice" },
    { id: "congress", label: "Congress Intelligence", icon: Presentation, tip: "Congress-sourced data readouts and reactions" },
    { id: "competitive", label: "Competitive Intelligence", icon: Scale, tip: "Comparative landscape and competitive uncertainty" },
    { id: "sources", label: "Source Library", icon: Library, tip: "The governed evidence repository behind every finding" },
  ]},
  { label: "Synthesise & Act", items: [
    { id: "consensus", label: "Consensus & Controversies", icon: GitCompareArrows, tip: "Where views converge, diverge or remain uncertain" },
    { id: "signals", label: "Emerging Signals", icon: Radar, tip: "Potential changes requiring validation and monitoring" },
    { id: "strategy", label: "Strategic Implications", icon: Target, tip: "What the findings imply — reviewed decision support" },
    { id: "reports", label: "Reports", icon: FileBarChart2, tip: "Build, review, approve and export intelligence reports" },
  ]},
];

export const GOVERNANCE_GROUP = { label: "Governance", items: [
  { id: "evidence-upload", label: "Evidence Upload & Analysis", icon: FileUp, tip: "Governed source intake and AI extraction review" },
  { id: "calibration", label: "Calibration & Scoring", icon: Gauge, tip: "Confidence and salience frameworks, caps and weights" },
  { id: "integrity", label: "Data Integrity", icon: ShieldCheck, tip: "Controls, integrity checks, exceptions and audit evidence" },
  { id: "model", label: "Information Model", icon: Boxes, tip: "The governed object registry, linkage matrix and totals reconciliation" },
  { id: "admin", label: "Admin & Review", icon: ClipboardCheck, tip: "Scientific review queues and project administration" },
  { id: "analytics", label: "Analytics", icon: BarChart3, tip: "First-party site analytics — traffic, conversions and devices" },
]};

// Illustrative demonstration counts — actionable badges only
export const NAV_BADGES = { admin: 3, integrity: 5, ai: 5 };

export const visibleGovernance = (role) => role?.group === "Platform";
export const ALL_ITEMS = [...NAV_GROUPS.flatMap((g) => g.items), ...GOVERNANCE_GROUP.items];
export const visibleIds = (role) => [
  ...NAV_GROUPS.flatMap((g) => g.items.map((i) => i.id)),
  ...(visibleGovernance(role) ? GOVERNANCE_GROUP.items.map((i) => i.id) : []),
  "ai",
];

export const LogoMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-label="HCP Voice mark" role="img">
    <rect x="1" y="1" width="38" height="38" rx="9" fill="rgba(20,184,166,0.12)" stroke="rgba(20,184,166,0.45)" />
    {/* voice waveform resolving into evidence nodes */}
    <path d="M7 20 L9.5 20 L11.5 14.5 L14 25.5 L16.5 11 L19 27 L21.5 17 L24 22.5 L26.5 20 L30 20"
      stroke="#2DD4BF" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx="30" cy="20" r="2.1" fill="#2DD4BF" />
    <circle cx="34" cy="14.5" r="1.7" fill="#5EEAD4" />
    <circle cx="34" cy="25.5" r="1.7" fill="#0D9488" />
    <path d="M30 20 L34 14.5 M30 20 L34 25.5" stroke="#2DD4BF" strokeWidth="1.1" strokeLinecap="round" opacity="0.75" />
  </svg>
);

export const BrandLockup = ({ compact = false }) => (
  <div className="flex items-center gap-3 min-w-0">
    <div className="shrink-0"><LogoMark size={compact ? 32 : 36} /></div>
    {!compact && (
      <div className="min-w-0">
        <p className="font-heading font-extrabold text-white text-lg leading-none tracking-tight">HCP Voice</p>
        <p className="text-[8.5px] font-mono uppercase tracking-[0.14em] text-slate-400 mt-1.5 truncate">Healthcare Professional Intelligence</p>
      </div>
    )}
  </div>
);

const Badge = ({ n }) => n > 0 ? (
  <span className="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[9.5px] font-bold flex items-center justify-center" title="Illustrative count">{n}</span>
) : null;

const NavButton = ({ id, label, icon: Icon, tip, active, onNavigate, collapsed, badge, tid = "nav" }) => (
  <button
    key={id}
    data-testid={`${tid}-${id}`}
    onClick={() => onNavigate(id)}
    aria-current={active ? "page" : undefined}
    title={collapsed ? `${label} — ${tip}` : tip}
    className={`relative w-full flex items-center gap-2.5 rounded-lg text-[13px] font-medium text-left transition-colors duration-150 outline-none
      focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900
      ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2"}
      ${active ? "bg-teal-500/10 text-teal-300" : "text-slate-400 hover:bg-slate-800/70 hover:text-white"}`}
  >
    {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-teal-400" aria-hidden />}
    <Icon className="w-4 h-4 shrink-0" />
    {!collapsed && <span className="flex-1 min-w-0 truncate">{label}</span>}
    {!collapsed && <Badge n={badge || 0} />}
    {collapsed && badge > 0 && <span className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-400" aria-hidden />}
  </button>
);

const ProjectContext = ({ collapsed, project }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button data-testid="project-context-btn" title="Active project — open project switcher" aria-label="Active project ORSERDU — open project switcher"
        className={`w-full text-left border-t border-slate-800 hover:bg-slate-800/60 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-teal-400 ${collapsed ? "px-2 py-3 flex justify-center" : "px-5 py-3"}`}>
        {collapsed ? (
          <span className="font-heading text-[10px] font-extrabold text-teal-300">OR</span>
        ) : (
          <>
            <p className="text-xs font-bold text-slate-100">ORSERDU · <span className="font-medium text-slate-300">Elacestrant</span></p>
            <p className="text-[10px] text-slate-400 mt-0.5">Global · ER+/HER2− · ESR1-mutated</p>
            <p className="text-[10px] text-slate-400">In Review · Cut-off 14 Jun 2026</p>
          </>
        )}
      </button>
    </PopoverTrigger>
    <PopoverContent side="top" align="start" className="w-72 p-3" data-testid="project-switcher">
      <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Project switcher</p>
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-2.5">
        <p className="text-xs font-bold text-slate-900">ORSERDU <span className="font-medium text-slate-500">(brand)</span> · elacestrant <span className="font-medium text-slate-500">(generic asset)</span></p>
        <p className="text-[10.5px] text-slate-500 mt-0.5">Global · ER+/HER2−, ESR1-mutated advanced BC · Cross-sectional assessment</p>
        <p className="text-[10.5px] text-teal-700 font-semibold mt-0.5">Active · In Review · Evidence cut-off 14 Jun 2026</p>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Additional projects (other assets and indications) require administrator provisioning. Illustrative prototype — single project configured.</p>
    </PopoverContent>
  </Popover>
);

export const Sidebar = ({ active, onNavigate, role, project }) => {
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem("hcp_nav_collapsed") === "1");
  const [govOpen, setGovOpen] = useState(() => sessionStorage.getItem("hcp_gov_open") !== "0");
  const showGov = visibleGovernance(role);
  const toggleCollapsed = () => { const v = !collapsed; setCollapsed(v); sessionStorage.setItem("hcp_nav_collapsed", v ? "1" : "0"); };
  const toggleGov = () => { const v = !govOpen; setGovOpen(v); sessionStorage.setItem("hcp_gov_open", v ? "1" : "0"); };

  return (
    <aside className={`hidden lg:flex flex-col shrink-0 bg-slate-900 text-slate-300 min-h-screen sticky top-0 h-screen transition-[width] duration-200 ${collapsed ? "w-[76px]" : "w-[272px]"}`} data-testid="sidebar">
      <div className={`flex items-center border-b border-slate-800 ${collapsed ? "justify-center px-2 py-4" : "justify-between px-5 py-4"}`}>
        <BrandLockup compact={collapsed} />
        {!collapsed && (
          <button onClick={toggleCollapsed} data-testid="sidebar-collapse-btn" title="Collapse sidebar" aria-label="Collapse sidebar"
            className="text-slate-500 hover:text-white p-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-teal-400">
            <PanelLeftClose className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {collapsed && (
        <button onClick={toggleCollapsed} data-testid="sidebar-expand-btn" title="Expand sidebar" aria-label="Expand sidebar"
          className="mx-auto mt-2 text-slate-500 hover:text-white p-1.5 rounded outline-none focus-visible:ring-2 focus-visible:ring-teal-400">
          <PanelLeft className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
      <nav className={`flex-1 overflow-y-auto sidebar-scroll py-3 space-y-4 ${collapsed ? "px-2" : "px-3"}`} aria-label="Primary">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            {!collapsed && <p className="px-3 mb-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{group.label}</p>}
            <div className="space-y-0.5">
              {group.items.map((it) => (
                <NavButton key={it.id} {...it} active={active === it.id} onNavigate={onNavigate} collapsed={collapsed} badge={NAV_BADGES[it.id]} />
              ))}
            </div>
          </div>
        ))}
        {showGov && (
          <div>
            {!collapsed ? (
              <button onClick={toggleGov} data-testid="governance-toggle"
                className="w-full flex items-center gap-1 px-3 mb-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500 hover:text-slate-300 outline-none focus-visible:ring-2 focus-visible:ring-teal-400 rounded">
                {govOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}Governance
              </button>
            ) : <div className="border-t border-slate-800 mx-2 mb-1" />}
            {(govOpen || collapsed) && (
              <div className="space-y-0.5" data-testid="governance-items">
                {GOVERNANCE_GROUP.items.map((it) => (
                  <NavButton key={it.id} {...it} active={active === it.id} onNavigate={onNavigate} collapsed={collapsed} badge={NAV_BADGES[it.id]} />
                ))}
              </div>
            )}
          </div>
        )}
      </nav>
      <div className={collapsed ? "px-2 pb-2" : "px-3 pb-2"}>
        <button
          data-testid="nav-ai"
          onClick={() => onNavigate("ai")}
          title="AI Analyst — outputs are AI-derived interpretations and require scientific review; opening the console does not create an approved finding"
          aria-current={active === "ai" ? "page" : undefined}
          className={`w-full flex items-center gap-2 rounded-lg text-[12px] transition-colors border outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900
            ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2"}
            ${active === "ai" ? "bg-violet-500/15 text-violet-300 border-violet-500/40" : "bg-slate-800/50 text-slate-400 border-slate-700/70 hover:text-violet-300 hover:border-violet-500/40"}`}
        >
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          {!collapsed && <span className="flex-1 text-left font-semibold">Open AI Analyst</span>}
          {!collapsed && <Badge n={NAV_BADGES.ai} />}
        </button>
        {!collapsed && <p className="px-1 mt-1 text-[8.5px] text-slate-600 leading-snug">AI-derived interpretation — requires scientific review</p>}
      </div>
      <ProjectContext collapsed={collapsed} project={project} />
    </aside>
  );
};

export const MobileNav = ({ active, onNavigate, role }) => {
  const [open, setOpen] = useState(false);
  const showGov = visibleGovernance(role);
  const current = ALL_ITEMS.find((i) => i.id === active) || { label: active === "ai" ? "AI Analyst Console" : "" };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);

  const go = (id) => { onNavigate(id); setOpen(false); };

  return (
    <div className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-slate-900/95 border-b border-slate-800" data-testid="mobile-nav">
      <div className="flex items-center gap-3 px-3 py-2">
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Open navigation"
          className="w-11 h-11 flex items-center justify-center text-slate-300 hover:text-white rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-teal-400">
          <Menu className="w-5 h-5" />
        </button>
        <BrandLockup compact />
        <span className="font-heading font-extrabold text-white text-sm">HCP Voice</span>
        <span className="text-[11px] text-slate-400 truncate ml-auto">{current.label}</span>
      </div>
      {open && createPortal(
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-950/70" onClick={() => setOpen(false)} data-testid="drawer-backdrop" />
          <div className="absolute inset-y-0 left-0 w-[300px] max-w-[85vw] bg-slate-900 border-r border-slate-800 overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <BrandLockup />
              <button onClick={() => setOpen(false)} aria-label="Close navigation" data-testid="drawer-close"
                className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-white rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-teal-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="px-3 py-3 space-y-4" aria-label="Primary">
              {[...NAV_GROUPS, ...(showGov ? [GOVERNANCE_GROUP] : [])].map((group) => (
                <div key={group.label}>
                  <p className="px-3 mb-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{group.label}</p>
                  <div className="space-y-0.5">
                    {group.items.map((it) => (
                      <NavButton key={it.id} {...it} active={active === it.id} onNavigate={go} collapsed={false} badge={NAV_BADGES[it.id]} tid="drawer-nav" />
                    ))}
                  </div>
                </div>
              ))}
              <button data-testid="mobile-nav-ai" onClick={() => go("ai")}
                className={`w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-[12px] font-semibold border ${active === "ai" ? "bg-violet-500/15 text-violet-300 border-violet-500/40" : "bg-slate-800/50 text-slate-400 border-slate-700/70"}`}>
                <Sparkles className="w-3.5 h-3.5" />Open AI Analyst<Badge n={NAV_BADGES.ai} />
              </button>
            </nav>
            <ProjectContext collapsed={false} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
